'use client';

import type { ToasterProps } from 'sonner';
import { Toaster as SonnerToaster, toast as sonnerToast, useSonner } from 'sonner';

import { cx } from '../../../utils/cx';
import { IconNotification } from './notifications';

export const DEFAULT_TOAST_POSITION = 'bottom-right';

/** Re-exported from the package's own sonner instance so callers fire toasts into <Toaster />. */
export { sonnerToast as toast };

export type NotifyColor = 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';

export interface NotifyOptions {
  title: string;
  description?: string;
  color?: NotifyColor;
  /** Auto-dismiss duration in ms. Omit to use the Toaster default. */
  duration?: number;
  /** Override the toast position for this toast only (defaults to the <Toaster /> position). */
  position?: ToasterProps['position'];
}

/**
 * Fire an Untitled UI IconNotification toast. Renders into the nearest <Toaster />.
 *
 * @example notify({ title: 'Changes saved', description: '...', color: 'success' })
 */
export const notify = ({
  title,
  description = '',
  color = 'default',
  duration,
  position,
}: NotifyOptions) =>
  sonnerToast.custom(
    (id) => (
      <IconNotification
        title={title}
        description={description}
        color={color}
        hideDismissLabel
        onClose={() => sonnerToast.dismiss(id)}
      />
    ),
    { duration, position },
  );

export const ToastsOverlay = () => {
  const { toasts } = useSonner();

  const styles = {
    'top-right': {
      className: 'top-0 right-0',
      background: 'linear-gradient(215deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 50%)',
    },
    'top-left': {
      className: 'top-0 left-0',
      background: 'linear-gradient(139deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 40.64%)',
    },
    'bottom-right': {
      className: 'bottom-0 right-0',
      background: 'linear-gradient(148deg, rgba(0, 0, 0, 0.00) 58.58%, rgba(0, 0, 0, 0.10) 97.86%)',
    },
    'bottom-left': {
      className: 'bottom-0 left-0',
      background: 'linear-gradient(214deg, rgba(0, 0, 0, 0.00) 54.54%, rgba(0, 0, 0, 0.10) 95.71%)',
    },
  };

  // Deduplicated list of positions
  const positions = toasts.reduce<NonNullable<ToasterProps['position']>[]>((acc, t) => {
    acc.push(t.position || DEFAULT_TOAST_POSITION);
    return acc;
  }, []);

  return (
    <>
      {Object.entries(styles).map(([position, style]) => (
        <div
          key={position}
          className={cx(
            'pointer-events-none fixed z-40 hidden h-72.5 w-130 transition duration-500 xs:block',
            style.className,
            positions.includes(position as keyof typeof styles)
              ? 'visible opacity-100'
              : 'invisible opacity-0',
          )}
          style={{
            background: style.background,
          }}
        />
      ))}
      <div
        className={cx(
          'pointer-events-none fixed right-0 bottom-0 left-0 z-40 h-67.5 w-full bg-linear-to-t from-black/10 to-transparent transition duration-500 xs:hidden',
          positions.length > 0 ? 'visible opacity-100' : 'invisible opacity-0',
        )}
      />
    </>
  );
};

export const Toaster = ({
  position,
  style,
}: {
  position?: ToasterProps['position'];
  style?: React.CSSProperties;
}) => (
  <>
    <SonnerToaster
      position={position ?? DEFAULT_TOAST_POSITION}
      style={
        {
          '--width': '400px',
          ...style,
        } as React.CSSProperties
      }
    />
    <ToastsOverlay />
  </>
);
